import React, { useMemo } from "react";

// Parse plain CV text into a structured document for styled rendering
export function parseCV(text) {
  const lines = (text || "").split("\n").map((l) => l.replace(/\r$/, ""));
  const blocks = [];
  let i = 0;

  // First non-empty = name
  while (i < lines.length && !lines[i].trim()) i++;
  const name = (lines[i] || "").trim();
  i++;

  // Optional subtitle line (e.g. role / tagline) — keep if it contains | or • or is short and not all caps
  let subtitle = "";
  if (i < lines.length && lines[i].trim() && /[|•·]/.test(lines[i])) {
    subtitle = lines[i].trim();
    i++;
  }

  // Contact block until blank
  const contact = [];
  while (i < lines.length && lines[i].trim()) {
    contact.push(lines[i].trim());
    i++;
  }

  blocks.push({ type: "header", name, subtitle, contact });

  const isSectionHeading = (s) => {
    if (!s) return false;
    const t = s.trim();
    if (t.length < 3 || t.length > 60) return false;
    const letters = [...t].filter((c) => /[A-Za-z]/.test(c));
    if (letters.length < 3) return false;
    return letters.every((c) => c === c.toUpperCase());
  };
  const isBullet = (s) => /^[•\-*]\s+/.test(s.trim());
  const looksLikeDateLine = (s) =>
    /\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\b/i.test(s) ||
    /\b(20\d{2})\b/.test(s) ||
    /\b(present|current)\b/i.test(s);

  let currentSection = null;
  let buffer = [];

  const flushBuffer = () => {
    if (buffer.length) {
      currentSection.items.push(...buffer);
      buffer = [];
    }
  };

  for (; i < lines.length; i++) {
    const raw = lines[i];
    const s = raw.trim();
    if (!s) {
      if (currentSection) flushBuffer();
      continue;
    }
    if (isSectionHeading(s)) {
      if (currentSection) {
        flushBuffer();
        blocks.push(currentSection);
      }
      currentSection = { type: "section", title: s, items: [] };
      continue;
    }
    if (!currentSection) {
      currentSection = { type: "section", title: "", items: [] };
    }
    if (isBullet(s)) {
      currentSection.items.push({ kind: "bullet", text: s.replace(/^[•\-*]\s+/, "") });
    } else if (looksLikeDateLine(s) && currentSection.items.length > 0) {
      const prev = currentSection.items[currentSection.items.length - 1];
      if (prev && prev.kind === "role" && !prev.meta) {
        prev.meta = s;
      } else {
        currentSection.items.push({ kind: "meta", text: s });
      }
    } else if (
      s.length < 110 &&
      i + 1 < lines.length &&
      looksLikeDateLine(lines[i + 1] || "")
    ) {
      currentSection.items.push({ kind: "role", text: s });
    } else {
      currentSection.items.push({ kind: "line", text: s });
    }
  }
  if (currentSection) {
    flushBuffer();
    blocks.push(currentSection);
  }

  return blocks;
}

export function CVDocument({ text }) {
  const blocks = useMemo(() => parseCV(text), [text]);
  return (
    <div className="doc-shell" data-testid="cv-document-view">
      <div className="doc-paper">
        {blocks.map((b, idx) => {
          if (b.type === "header") {
            return (
              <div key={idx}>
                <div className="doc-name">{b.name}</div>
                {b.subtitle && <div className="doc-title-line">{b.subtitle}</div>}
                {b.contact.map((c, i) => (
                  <div key={i} className="doc-contact">
                    {c}
                  </div>
                ))}
                <div className="doc-rule"></div>
              </div>
            );
          }
          if (b.type === "section") {
            return (
              <div key={idx}>
                {b.title && <div className="doc-section">{b.title}</div>}
                {b.items.map((it, i) => {
                  if (it.kind === "bullet") return <div key={i} className="doc-bullet"><span>{it.text}</span></div>;
                  if (it.kind === "role") return (
                    <div key={i}>
                      <div className="doc-role-title">{it.text}</div>
                      {it.meta && <div className="doc-role-meta">{it.meta}</div>}
                    </div>
                  );
                  if (it.kind === "meta") return <div key={i} className="doc-role-meta">{it.text}</div>;
                  return <div key={i} className="doc-line">{it.text}</div>;
                })}
              </div>
            );
          }
          return null;
        })}
      </div>
    </div>
  );
}

export function CoverLetterDocument({ text }) {
  const paragraphs = (text || "").split(/\n\s*\n/);
  return (
    <div className="doc-shell" data-testid="cover-document-view">
      <div className="doc-paper cover-letter">
        {paragraphs.map((p, i) => (
          <div key={i} className="cover-line" style={{ whiteSpace: "pre-wrap" }}>
            {p}
            <div className="cover-spacer"></div>
          </div>
        ))}
      </div>
    </div>
  );
}
